import React from 'react'
import { motion } from 'framer-motion'
import { 
  Monitor, 
  Smartphone, 
  Server,
  Database,
  Wifi,
  Shield,
  Globe,
  Zap
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

export const TechArchitecture: React.FC = () => {
  const { t } = useTranslation('workforce')
  return (
    <section className="py-24 bg-gray-900 text-white">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold mb-4"
          >
            {t('techArchitecture.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            {t('techArchitecture.subtitle')}
          </motion.p>
        </div>

        {/* Architecture Diagram */}
        <div className="max-w-6xl mx-auto mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            
            {/* Frontend Layer */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-8 mb-4">
                <Monitor className="w-12 h-12 text-white mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">{t('techArchitecture.frontend.title')}</h3>
                <p className="text-blue-100 text-sm mb-4">{t('techArchitecture.frontend.subtitle')}</p>
                
                <div className="space-y-2">
                  {(t('techArchitecture.frontend.technologies', { returnObjects: true }) as string[]).map((tech: string, index: number) => (
                    <div key={index} className="bg-white/10 rounded px-3 py-1 text-sm">{tech}</div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-orange-600 to-orange-800 rounded-2xl p-6">
                <Smartphone className="w-10 h-10 text-white mx-auto mb-3" />
                <h4 className="font-bold mb-2">{t('techArchitecture.mobile.title')}</h4>
                <div className="text-sm text-orange-100">{t('techArchitecture.mobile.subtitle')}</div>
              </div>
            </motion.div>

            {/* Integration Layer */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="bg-gradient-to-br from-orange-600 to-red-600 rounded-2xl p-8">
                <Zap className="w-12 h-12 text-white mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">{t('techArchitecture.integration.title')}</h3>
                <p className="text-orange-100 text-sm mb-6">{t('techArchitecture.integration.subtitle')}</p>
                
                <div className="space-y-3">
                  {(t('techArchitecture.integration.technologies', { returnObjects: true }) as string[]).map((tech: string, index: number) => {
                    const icons = [<Wifi key="wifi" className="w-4 h-4" />, <Globe key="globe" className="w-4 h-4" />, <Shield key="shield" className="w-4 h-4" />];
                    return (
                      <div key={index} className="flex items-center justify-center gap-2">
                        {icons[index]}
                        <span className="text-sm">{tech}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>

            {/* Backend Layer */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-center"
            >
              <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-2xl p-8 mb-4">
                <Server className="w-12 h-12 text-white mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">{t('techArchitecture.backend.title')}</h3>
                <p className="text-green-100 text-sm mb-4">{t('techArchitecture.backend.subtitle')}</p>
                
                <div className="space-y-2">
                  {(t('techArchitecture.backend.technologies', { returnObjects: true }) as string[]).map((tech: string, index: number) => (
                    <div key={index} className="bg-white/10 rounded px-3 py-1 text-sm">{tech}</div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl p-6">
                <Database className="w-10 h-10 text-white mx-auto mb-3" />
                <h4 className="font-bold mb-2">{t('techArchitecture.database.title')}</h4>
                <div className="text-sm text-purple-100">{t('techArchitecture.database.subtitle')}</div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Tech Stack Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6"
        >
          {(t('techArchitecture.tech', { returnObjects: true }) as Array<{ name: string, emoji: string }>).map((tech, index) => (
            <motion.div
              key={tech.name}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-colors"
            >
              <div className="text-3xl mb-2">{tech.emoji}</div>
              <div className="text-sm font-medium">{tech.name}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}